import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { BlogOverviewSidebar } from '@/components/blog/BlogOverviewSidebar';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';

export default function Blog() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const tagFilter = searchParams.get('tag');
  
  const { data: posts, isLoading, error } = useQuery({
    queryKey: ['public-blog-posts', categoryFilter, tagFilter],
    queryFn: async () => {
      let query = supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, created_at, meta_description, category, tags')
        .eq('published', true)
        .order('created_at', { ascending: false });

      // Apply category filter
      if (categoryFilter) {
        // Normalisiere den Filter (z.B. "cyberversicherung" → "Cyberversicherung")
        const categoryName = categoryFilter
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        query = query.ilike('category', categoryName);
      }

      // Apply tag filter
    if (tagFilter) {
      // Kapitalisiere den Tag-Namen (z.B. "versicherung" → "Versicherung")
      const tagName = tagFilter
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      query = query.contains('tags', [tagName]);
    }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    }
  });

  // Excerpt generieren falls nicht vorhanden
  const getExcerpt = (post: any) => {
    // Priorisierung: excerpt > meta_description > Standardtext
    if (post.excerpt && post.excerpt.trim() !== '') return post.excerpt;
    if (post.meta_description && post.meta_description.trim() !== '') return post.meta_description;
    return 'Lesen Sie mehr in diesem interessanten Artikel über Cyberversicherungen für Arztpraxen.';
  };

  if (isLoading) {
    return (
      <>
        <Navigation />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Blog</h1>
            <p className="text-muted-foreground">Lädt Artikel...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navigation />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Blog</h1>
            <p className="text-destructive">
              Fehler beim Laden der Artikel. Bitte versuchen Sie es später erneut.
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation />
      <PageBreadcrumbs items={[
        { label: "Home", path: "/" },
        { label: "Blog" }
      ]} />
      
      {/* Wellenförmiger Hintergrund */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
      </div>

      <div className="min-h-screen relative z-10">
        {/* Header */}
        <div className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 border-b border-white/5">
          <div className="container mx-auto text-center max-w-4xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-foreground">
              Blog
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground/90 leading-relaxed px-4 sm:px-0">
              Aktuelle Insights, Tipps und News rund um Cyberversicherungen für Arztpraxen
            </p>
          </div>
        </div>

        {/* Cards Grid with Sidebar */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 sm:gap-8 max-w-7xl mx-auto">
            {/* Main Content - Blog Posts */}
            <div className="space-y-6 sm:space-y-8">
              {posts && posts.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 sm:gap-5 md:gap-6">
                  {posts.map((post) => (
                    <Card 
                      key={post.id} 
                      className="group h-full flex flex-col overflow-hidden relative
                        min-h-[280px] sm:min-h-[300px]
                        backdrop-blur-xl
                        bg-white/[0.115]
                        border-2 border-accent/40
                        rounded-[33px]
                        shadow-[2px_8px_8px_rgba(0,0,0,0.3),0_0_20px_rgba(139,195,74,0.1)]
                        hover:shadow-[4px_12px_12px_rgba(0,0,0,0.4),0_0_30px_rgba(139,195,74,0.2)]
                        hover:border-accent/60
                        after:content-[''] 
                        after:absolute 
                        after:inset-0
                        after:bg-gradient-to-b after:from-accent/8 after:via-transparent after:to-transparent
                        after:opacity-0 group-hover:after:opacity-100
                        after:transition-opacity after:duration-500
                        after:pointer-events-none
                        after:z-[1]
                        after:rounded-[33px]
                        transition-all duration-300 ease-out
                        [&>*]:relative [&>*]:z-10"
                    >
                      <CardHeader className="pb-3 sm:pb-4 px-4 sm:px-5 lg:px-6 pt-4 sm:pt-5 lg:pt-6">
                        {/* Datum */}
                        <div className="text-xs sm:text-sm text-slate-300/90 mb-2 sm:mb-3 font-medium">
                          {new Date(post.created_at).toLocaleDateString('de-DE', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </div>
                        
                        {/* Titel mit automatischem Umbruch */}
                        <CardTitle 
                          className="text-lg sm:text-xl lg:text-2xl font-bold leading-tight mb-2 sm:mb-3 
                            text-white
                            group-hover:text-accent transition-colors
                            line-clamp-2 break-words hyphens-auto"
                          lang="de"
                        >
                          {post.title}
                        </CardTitle>
                      </CardHeader>
                      
                      <CardContent className="flex-1 flex flex-col pt-0 px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 lg:pb-6">
                        {/* Excerpt - stark verkleinert */}
                        <p 
                          className="text-xs sm:text-sm text-slate-400/80 mb-4 sm:mb-6 flex-1 
                            leading-relaxed line-clamp-3 break-words hyphens-auto"
                          lang="de"
                        >
                          {getExcerpt(post)}
                        </p>
                        
                        {/* Button kompakt mit Icon */}
                        <Button 
                          variant="outline"
                          size="default"
                          className="self-start w-auto text-sm font-semibold px-4 h-10 rounded-lg"
                          onClick={() => navigate(`/blog/${post.slug}`)}
                        >
                          Artikel lesen
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <h2 className="text-3xl font-bold mb-4">
                    {categoryFilter || tagFilter ? 'Keine Artikel gefunden' : 'Noch keine Artikel'}
                  </h2>
                  <p className="text-muted-foreground text-lg">
                    {categoryFilter || tagFilter 
                      ? 'Für diesen Filter sind keine Artikel verfügbar.'
                      : 'Die ersten Blog-Artikel sind in Vorbereitung.'}
                  </p>
                </div>
              )}
            </div>
            
            {/* Sidebar */}
            <aside className="lg:sticky lg:top-24 h-fit order-first lg:order-last">
              <BlogOverviewSidebar />
            </aside>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
